import React from 'react';
import { Box, Stack } from '@mui/material';
import { grey } from '@mui/material/colors';
import Typography from '@mui/material/Typography';

import { formatCurrency } from '../../../../utils';
import { capitalizeFirstLetter } from '../../../../formatters';
import { GroupsSummaryData } from '../../types';

interface Props {
  summary: GroupsSummaryData | undefined;
}

export const ReportsSummary = ({ summary }: Props) => {
  if (!summary) return null;

  return (
    <Box sx={{ boxShadow: ({ shadows }) => shadows[1] }}>
      <Stack gap="0.5rem" sx={{ backgroundColor: grey[50], borderRadius: '0.5rem', padding: '1rem' }}>
        <Stack direction="row" width="100%" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" component="span">
            Report
          </Typography>

          <Stack direction="row" gap="1rem" flexWrap="wrap">
            <Stack direction="row" alignItems="center" gap="0.5rem">
              <Typography variant="body2">Total Records: </Typography>

              <Typography variant="subtitle1" fontWeight="bold">
                {summary?.dealsAmount}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" gap="0.5rem">
              <Typography variant="body2">Total Amount: </Typography>

              <Typography variant="subtitle1" fontWeight="bold" color="success">
                {formatCurrency(summary?.dealsSum)}
              </Typography>
            </Stack>
          </Stack>
        </Stack>

        <Stack direction="row" gap="1rem" flexWrap="wrap">
          {Object.values(
            (summary?.data ?? []).reduce<
              Record<
                string,
                { stageId: string; stageName: string; department: string; dealsAmount: number; dealsSum: number }
              >
            >((acc, dataItem) => {
              const key = `${dataItem.stageName}_${dataItem.department}`;
              if (acc[key]) {
                acc[key].dealsAmount += dataItem.dealsAmount;
                acc[key].dealsSum += dataItem.dealsSum;
              } else {
                acc[key] = { ...dataItem };
              }
              return acc;
            }, {})
          ).map(dataItem => (
            <Stack
              width="15rem"
              direction="row"
              key={`${dataItem.stageName}_${dataItem.department}`}
              justifyContent="space-between"
              sx={{
                flex: '1 1 15rem',
                padding: '1rem',
                borderRadius: '0.5rem',
                backgroundColor: 'white',
                boxShadow: ({ shadows }) => shadows[1],
              }}
            >
              <Stack gap="0.5rem">
                <Typography color="textDisabled">{dataItem.stageName}</Typography>

                <Typography variant="subtitle1" fontWeight="bold">
                  {dataItem.dealsAmount}
                </Typography>
              </Stack>

              <Stack gap="0.5rem">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: 'fit-content',
                    borderRadius: '0.2rem',
                    padding: '0.1rem 0.2rem',
                    justifyContent: 'center',
                    backgroundColor: grey[200],
                  }}
                >
                  <Typography variant="caption">{capitalizeFirstLetter(dataItem.department)}</Typography>
                </Box>

                <Typography fontWeight="bold">{formatCurrency(dataItem.dealsSum)}</Typography>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};
